import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import ClientsTable from './sections/ClientsTable/ClientsTable';
import { ReactComponent as ClientIcon } from '../../assets/icons/icon-client.svg';
import { ReactComponent as ErrorIcon } from '../../assets/icons/xerror.svg';
import CustomButton from '../../shared/components/Buttons/CustomButton/CustomButton';
import BannerFill from '../../shared/components/Globals/BannerFill/BannerFill';
import EmptyState from '../../shared/components/Globals/EmptyState/EmptyState';
import FilterBar, { FilterPill } from '../../shared/components/Globals/FilterBar/FilterBar';
import PrimaryHeader from '../../shared/components/Globals/Headers/PrimaryHeader/PrimaryHeader';
import SearchBar from '../../shared/components/Globals/SearchBar/SearchBar';
import { buildClientProfileRoute } from '../../shared/constants/auth';
import { CLIENTS_COLUMNS } from '../../shared/constants/clients';
import {
  CLICK_ACTION,
  CLIENT_SORT_COLUMN,
  INPUT_ACTION,
  SEARCH_CLIENTS,
  SEARCH_CLIENTS_DEBOUNCE_MS,
  SELECT_CLIENT_BUCKET,
  SORT_CLIENTS
} from '../../shared/constants/tagManager';
import { useClients } from '../../shared/hooks/useClients/useClients';
import type { IClient, IClientsLocationState } from '../../shared/interfaces/clients';
import TagManagerService from '../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_CLIENTS } from '../../shared/translations/clients';
import { ClientsStatus, type ClientsSortKey } from '../../shared/types/clients';
import { isClientsStatus } from '../../shared/utils/clients/clients';

import './Clients.scss';

const Clients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: IClientsLocationState | null };
  const initialStatus = isClientsStatus(state?.status) ? state.status : undefined;
  const {
    status,
    onStatusChange,
    clients,
    activationErrorClients,
    total,
    filteredTotal,
    loading,
    error,
    isFirstUse,
    query,
    onQueryChange,
    sort,
    onSortChange,
    page,
    pageSize,
    onPageChange,
    retry
  } = useClients({ initialStatus });

  const [bannerDismissed, setBannerDismissed] = useState(false);

  const STATUS_FILTERS: { value: ClientsStatus; labelKey: string }[] = [
    { value: ClientsStatus.Active, labelKey: TRANSLATIONS_CLIENTS.status.active },
    { value: ClientsStatus.AwaitingStart, labelKey: TRANSLATIONS_CLIENTS.status.awaitingStart }
  ];

  const handleSelect = (client: IClient) => {
    void navigate(buildClientProfileRoute(client.id, client.status), {
      state: { status: client.status, companyName: client.companyName, documentValue: client.documentValue }
    });
  };

  const searchTracked = useRef(false);

  useEffect(() => {
    if (!query.trim()) {
      searchTracked.current = false;
      return;
    }
    if (searchTracked.current) return;

    const handler = setTimeout(() => {
      searchTracked.current = true;
      TagManagerService.pushEvent(INPUT_ACTION, SEARCH_CLIENTS);
    }, SEARCH_CLIENTS_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [query]);

  const handleStatusChange = (next: ClientsStatus) => {
    if (next === status) return;
    TagManagerService.pushEvent(CLICK_ACTION, SELECT_CLIENT_BUCKET, { bucket: next });
    onStatusChange(next);
  };

  const handleSortChange = (key: ClientsSortKey) => {
    const column = CLIENT_SORT_COLUMN[key];
    if (column) {
      TagManagerService.pushEvent(CLICK_ACTION, SORT_CLIENTS, { column });
    }
    onSortChange(key);
  };

  const BANNER = TRANSLATIONS_CLIENTS.clientsList.activationErrorBanner;
  const failedCount = activationErrorClients.length;
  const bannerText =
    failedCount === 1
      ? t(BANNER.one, { company: activationErrorClients[0].companyName })
      : t(BANNER.many, { count: failedCount });

  const handleBannerView = () => {
    if (failedCount === 1) handleSelect(activationErrorClients[0]);
    else handleStatusChange(ClientsStatus.AwaitingStart);
  };

  const showErrorBanner = !isFirstUse && failedCount > 0 && !bannerDismissed;

  const showBannerAction = failedCount === 1 || status !== ClientsStatus.AwaitingStart;

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <PrimaryHeader title={t(TRANSLATIONS_CLIENTS.title)}>
        {!isFirstUse && <span className="clients__count">{t(TRANSLATIONS_CLIENTS.count, { total })}</span>}
      </PrimaryHeader>

      <div className="clients">
        {isFirstUse ? (
          <div className="clients__content clients__content--empty-state">
            <EmptyState
              icon={<ClientIcon width={28} height={28} aria-hidden="true" />}
              title={t(TRANSLATIONS_CLIENTS.clientsList.states.empty.title)}
              description={t(TRANSLATIONS_CLIENTS.clientsList.states.empty.description)}
            />
          </div>
        ) : (
          <>
            {showErrorBanner && (
              <div className="clients__error-banner">
                <BannerFill
                  variant="danger"
                  inline
                  regularText
                  icon={<ErrorIcon aria-hidden="true" />}
                  title={bannerText}
                  onClose={() => setBannerDismissed(true)}
                  closeLabel={t(BANNER.dismiss)}
                  trailing={
                    showBannerAction ? (
                      <CustomButton variant="default" size="s" onClick={handleBannerView}>
                        {t(BANNER.action)}
                      </CustomButton>
                    ) : undefined
                  }
                />
              </div>
            )}
            <div className="clients__toolbar">
              <FilterBar className="clients__tabs">
                {STATUS_FILTERS.map((filter) => (
                  <FilterPill
                    key={filter.value}
                    active={status === filter.value}
                    onClick={() => handleStatusChange(filter.value)}
                  >
                    {t(filter.labelKey)}
                  </FilterPill>
                ))}
              </FilterBar>

              <SearchBar
                className="clients__search"
                value={query}
                onChange={onQueryChange}
                placeholder={t(TRANSLATIONS_CLIENTS.clientsList.searchPlaceholder)}
              />
            </div>

            <div className="clients__content">
              <ClientsTable
                clients={clients}
                columns={CLIENTS_COLUMNS[status]}
                status={status}
                loading={loading}
                error={error}
                query={query}
                sort={sort}
                onSortChange={handleSortChange}
                page={page}
                pageSize={pageSize}
                total={filteredTotal}
                onPageChange={onPageChange}
                onSelect={handleSelect}
                onRetry={retry}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Clients;
